import React, { useEffect, useState } from 'react';
import 'react-phone-number-input/style.css';

import { AsYouTypeFormatter } from 'google-libphonenumber';
import Appearance from 'styles/Appearance.js';
import PhoneInput from 'react-phone-number-input';
import Utils from 'files/Utils.js';

const TextField = React.forwardRef((props, ref) => {

    const {
        appendValue, autoCapitalize, autoComplete, autoCorrect, canClearField, expandWithText, fieldStyle, format, focused, insetLabel,  isSecure, multiple, onBlur, onChange, onClick, onKeyDown, onValidate, placeholder, prependValue, spellCheck, useDelay, value
    } = props;

    const [active, setActive] = useState(focused);
    const [countryCode, setCountryCode] = useState('US');
    const [formatter, setFormatter] = useState(null);
    const [hover, setHover] = useState(false);
    const [textTimeout, setTextTimeout] = useState(false);
    const [text, setText] = useState(value !== null && value !== undefined ? value : '');

    const onClearTextInput = () => {
        setText(null);
        if(typeof(onChange) === 'function') {
            onChange(null);
        }
    }
    const onFieldClick = () => {
        setActive(true);
        if(typeof(onClick) === 'function') {
            onClick();
        }
    }

    const onFieldBlur = () => {
        setActive(false);
        if(typeof(onBlur) === 'function') {
            onBlur();
        }
    }

    const onPhoneInputTextChange = text => {

        // clear previous timeout if present
        if(textTimeout) {
            clearTimeout(textTimeout);
        }

        // set text with timeout if applicable
        setText(text);
        setTextTimeout(setTimeout(() => {
            if(typeof(onChange) === 'function') {
                onChange(text);
            }
        }, useDelay === true ? 250 : 0));
    }

    const onTextChange = evt => {
        try {

            // clear previous timeout if present
            if(textTimeout) {
                clearTimeout(textTimeout);
            }

            // format next value with prepend and append values if applicable
            let next = evt.target.value.replace(prependValue || '', '').replace(appendValue || '', '');

            // check for specialty formatting
            switch(format) {
                case 'double':
                next = parseFloat(next.replace(/[^\d.-]/g,''));
                break;

                case 'integer':
                next = parseInt(next.replace(/[^\d.-]/g,''));
                break;

                case 'number':
                next = next.replace(/[^\d.-]/g,'');
                break;

                default:
                if(typeof(format) === 'function') {
                    next = format(next);
                }
            }

            // set text with timeout if applicable
            setText(next);
            setTextTimeout(setTimeout(async () => {
                if(typeof(onChange) === 'function') {
                    onChange(next.toString().length === 0 ? null : next);
                }
            }, useDelay === true ? 250 : 0));

        } catch(e) {
            console.error(e.message);
        }
    }

    const getContent = () => {
        if(format !== 'phone_number') {
            return getField();
        }
        return (
            <FieldContainer
            {...props}
            {...getDefaultContainerProps()}
            phoneInput={true}
            phoneInputText={text}>
                <PhoneInput
                countryCallingCodeEditable={false}
                defaultCountry={countryCode}
                international={true}
                onBlur={onFieldBlur}
                onChange={onPhoneInputTextChange}
                onFocus={onFieldClick}
                placeholder={placeholder}
                value={text}
                withCountryCallingCode={true}
                style={{
                    color: Appearance.colors.text(),
                    fontSize: getMobileFontSize(12),
                    marginTop: 8,
                    opacity: active ? 1 : 0,
                    width: '100%'
                }}/>
            </FieldContainer>
        )
    }

    const getDefaultContainerProps = () => ({
        active: active,
        onMouseEnter: () => setHover(true),
        onMouseLeave: () => setHover(true),
        onHoverChange: val => setHover(val),
        onValidate: getFieldContainerValidation(),
        text: text
    })

    const getField = () => {
        return (
            <FieldContainer
            {...props}
            {...getDefaultContainerProps()}>
                {getFieldComponent()}
            </FieldContainer>
        )
    }

    const getFieldComponent = () => {
        if(typeof(onClick) === 'function') {
            return (
                <span
                onClick={onFieldClick}
                className={'cursor-pointer'}
                style={{
                    flexGrow: 1,
                    borderWidth: 0,
                    fontSize: getMobileFontSize(12),
                    fontWeight: 500,
                    color: value ? Appearance.colors.text() : Appearance.colors.subText(),
                    backgroundColor: Appearance.colors.transparent,
                    paddingTop: insetLabel ? (value ? 15 : 32) : 0,
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    maxWidth: '100%',
                    ...fieldStyle
                }}>{value || placeholder}</span>
            )
        }
        if(isSecure || expandWithText !== true) {
            return (
                <div style={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%'
                }}>
                    <input
                    ref={ref}
                    autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
                    autoComplete={autoComplete === false ? 'off' : 'on'}
                    autoCorrect={autoCorrect === false ? 'off' : 'on'}
                    className={`dummy-field-input ${window.theme}`}
                    onBlur={onFieldBlur}
                    onClick={onFieldClick}
                    onChange={onTextChange}
                    onKeyDown={onKeyDown}
                    inputMode={getInputMode()}
                    placeholder={getPlaceholder()}
                    spellCheck={spellCheck === false ? 'false' : 'true'}
                    type={isSecure ? 'password' : 'text'}
                    value={`${prependValue || ''}${text || ''}${appendValue || ''}`}
                    style={{
                        flexGrow: 1,
                        border: 'none',
                        height: 'auto',
                        fontSize: getMobileFontSize(12),
                        fontWeight: 500,
                        color: Appearance.colors.text(),
                        backgroundColor: Appearance.colors.transparent,
                        paddingTop: insetLabel ? 15 : 0,
                        ...fieldStyle
                    }} />
                    {text.length > 0 && canClearField === true && (
                        <img
                        className={'text-button'}
                        onClick={onClearTextInput}
                        src={'images/close-button-light-small.png'}
                        style={{
                            width: 20,
                            height: 20,
                            marginLeft: 8
                        }} />
                    )}
                </div>
            )
        }
        return (
            <textarea
            ref={ref}
            autoCapitalize={autoCapitalize === false ? 'off' : autoCapitalize}
            autoComplete={autoComplete === false ? 'off' : 'on'}
            autoCorrect={autoCorrect === false ? 'off' : 'on'}
            className={`dummy-field-input ${window.theme}`}
            onBlur={onFieldBlur}
            onClick={onFieldClick}
            onChange={onTextChange}
            inputMode={getInputMode()}
            placeholder={getPlaceholder()}
            spellCheck={spellCheck === false ? 'false' : 'true'}
            type={isSecure ? 'password' : 'text'}
            value={`${prependValue || ''}${text || ''}${appendValue || ''}`}
            style={{
                border: 'none',
                width: '100%',
                height: 'auto',
                minHeight: 125,
                fontSize: getMobileFontSize(12),
                fontWeight: 500,
                resize: 'none',
                verticalAlign: 'middle',
                color: Appearance.colors.text(),
                padding: '8px 12px 8px 12px',
                backgroundColor: Appearance.colors.transparent,
                ...fieldStyle
            }} />
        )
    }

    const getFieldContainerValidation = () => {
        switch(format) {
            case 'email_address':
            return text => {
                return text && text.toString().match(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
            }

            default:
            return onValidate;
        }
    }

    const getInputMode = () => {
        if([ 'integer' ].includes(format)) {
            return 'numeric';
        }
        if([ 'double', 'integer', 'number', 'phone_number' ].includes(format)) {
            return 'decimal';
        }
        return null;
    }

    const getMobileFontSize = val => {
        return window.innerWidth < 767.98 ? 16 : val;
    }

    const getPlaceholder = () => {
        if(!insetLabel) {
            return placeholder;
        }
        return active && multiple ? (placeholder || 'Press enter after each phrase to add it to your list') : null;
    }

    const setupFormat = () => {
        if(format === 'phone_number') {
            try {
                // setup formatter for inline text formatting
                let formatter = new AsYouTypeFormatter(countryCode || 'US');
                setFormatter(formatter);

                // set default country code using navigator locale
                let locale = new Intl.Locale(navigator.language);
                setCountryCode(locale.region);

            } catch(e) {
                console.error(e.message);
                setCountryCode('US');
            }
        }
    }

    useEffect(() => {
        setupFormat();
    }, [format]);

    useEffect(() => {
        setActive(focused);
    }, [focused])

    useEffect(() => {
        setText(value || '')
    }, [value]);

    return getContent();
})

export default TextField;

export const getIcon = icon => {
    switch(icon) {

        case 'asterick':
        return 'fas fa-asterisk';

        case 'atom':
        return 'fa-solid fa-atom';

        case 'bed':
        return 'fa-solid fa-bed';

        case 'bullseye':
        return 'fa-solid fa-bullseye';

        case 'calendar':
        return 'fas fa-calendar-alt';

        case 'description':
        return 'fas fa-stream';

        case 'dumbbell':
        return 'fa-solid fa-dumbbell'

        case 'email':
        return 'fas fa-envelope';

        case 'file':
        return 'fas fa-file';

        case 'fingerprint':
        return 'fa-solid fa-fingerprint';

        case 'globe':
        return 'fas fa-globe-americas';

        case 'house':
        return 'fas fa-home';

        case 'image':
        return 'fas fa-camera';

        case 'location':
        return 'fas fa-location-arrow';

        case 'lock':
        return 'fas fa-lock';

        case 'name':
        case 'title':
        return 'fas fa-signature';

        case 'number':
        return 'fa-solid fa-list-ol';

        case 'office':
        return 'fas fa-briefcase'

        case 'payment':
        return 'fas fa-money-check-alt';

        case 'password':
        return 'fas fa-user-secret';

        case 'phone':
        return 'fas fa-phone';

        case 'search':
        return 'fas fa-search';

        case 'service-bell':
        return 'fa-solid fa-bell-concierge';

        case 'shield':
        return 'fas fa-shield-alt';

        case 'spouse':
        return 'fas fa-house-user';

        case 'user':
        return 'fas fa-user';

        case 'user-shield':
        return 'fas fa-user-shield';

        case 'wifi':
        return 'fas fa-wifi';

        default:
        return icon;
    }
}

const getIconComponent = props => {

    let { icon, iconStyle, insetLabel, loading } = props;
    if(loading === true) {
        return (
            <div style={{
                marginLeft: insetLabel ? 4 : 0,
                paddingRight: insetLabel ? 12 : 8
            }}>
                <img
                className={'textfield-loader'}
                src={`images/textfield-loader-${window.theme === 'dark' ? 'white' : 'grey'}.png`}
                style={{
                    width: 15,
                    height: 15,
                    ...iconStyle
                }}/>
            </div>
        )
    }
    let className = getIcon(icon);
    if(!className) {
        return null;
    }
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginLeft: insetLabel ? 4 : 0,
            paddingRight: insetLabel ? 12 : 8
        }}>
            <i
            className={className}
            style={{
                color: Appearance.colors.grey(),
                fontSize: 14,
                width: 15,
                ...iconStyle
            }}/>
        </div>
    )
}

export const FieldContainer = props => {

    const { active = false, appendComponent, children, className, containerStyle, expandWithText, focused, insetLabel, minimal, onHoverChange, onValidate, phoneInput, phoneInputText, prependComponent, text } = props;
    const [hover, setHover] = useState(false);

    const getConditionalIconComponent = () => {
        if(phoneInput !== true || active === false) {
            return getIconComponent(props);
        }
        return null;
    }

    const getMobileFieldHeight = val => {
        return window.innerWidth < 767.98 ? 55 : val;
    }

    useEffect(() => {
        if(typeof(onHoverChange) === 'function') {
            onHoverChange(hover);
        }
    }, [hover]);

    return (
        <div
        className={`dummy-field ${window.theme} ${active ? 'active' : ''} ${className || ''}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
            alignItems: 'center',
            backgroundColor: Appearance.colors.textfield(),
            borderColor: Appearance.colors.divider(),
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            height: expandWithText ? 'auto' : getMobileFieldHeight(45),
            position: 'relative',
            minHeight: getMobileFieldHeight(45),
            ...typeof(insetLabel) === 'string' && {
                paddingLeft: 10,
                borderRadius: 8
            },
            ...expandWithText === true && {
                padding: 0
            },
            ...containerStyle,
            ...minimal && {
                borderColor: Appearance.colors.transparent
            }
        }}>
            {prependComponent}
            {getConditionalIconComponent()}
            <div
            className={`inset-label-container ${active ? 'active' : ''}`}
            style={{
                width: '100%',
                minWidth: 0,
                ...phoneInput === true && {
                    paddingTop: active ? 6 : 8
                }
            }}>
                {children}
                {phoneInput === true && active === false && (
                    <label style={{
                        ...Appearance.textStyles.subTitle(),
                        color: Appearance.colors.text(),
                        fontWeight: 500,
                        paddingTop: 8,
                        ...Utils.isMobile() === true && {
                            fontSize: 16
                        }
                    }}>{phoneInputText}</label>
                )}
                {typeof(insetLabel) === 'string' && (
                    <label
                    className={`inset-label ${active ? 'active' : ''} ${focused || text ? 'filled' : ''}`}
                    style={{
                        ...Appearance.textStyles.subTitle(),
                        ...Utils.isMobile() === true && {
                            fontSize: 14
                        },
                        ...phoneInput === true && active === true && {
                            paddingLeft: 55
                        }
                    }}>{insetLabel}</label>
                )}
            </div>
            {appendComponent}
            {typeof(onValidate) === 'function' && onValidate(text) === true && (
                <img
                src={'images/checkmark-green-small.png'}
                style={{
                    width: 15,
                    height: 15,
                    objectFit: 'contain',
                    marginLeft: 8
                }} />
            )}
        </div>
    )
}
